import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

interface AdminResetPasswordModalProps {
  visible: boolean;
  onHide: () => void;
  onSuccess?: () => void;
  // _id from the users collection
  userId: string;
  // Display name shown in the UI
  name: string;
  // Role of the target user (for display)
  role: 'admin' | 'operator' | 'teacher' | 'student';
}

const ROLE_META: Record<string, { label: string; icon: string; color: string }> = {
  admin:    { label: 'Admin',    icon: 'pi-shield',              color: '#0f3d3e' },
  operator: { label: 'Operator', icon: 'pi-cog',                 color: '#1a5555' },
  teacher:  { label: 'Teacher',  icon: 'pi-book',                color: '#0f3d3e' },
  student:  { label: 'Student',  icon: 'pi-graduation-cap',      color: '#1a5555' },
};

const AdminResetPasswordModal: React.FC<AdminResetPasswordModalProps> = ({
  visible,
  onHide,
  onSuccess,
  userId,
  name,
  role,
}) => {
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState<string | null>(null);
  const [success, setSuccess]                 = useState(false);

  const [errors, setErrors] = useState<{ newPassword: string; confirmPassword: string }>({
    newPassword: '',
    confirmPassword: '',
  });

  const meta = ROLE_META[role] ?? ROLE_META.operator;

  const clearFieldError = (field: 'newPassword' | 'confirmPassword') => {
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = { newPassword: '', confirmPassword: '' };
    let ok = true;

    if (!newPassword.trim()) {
      e.newPassword = 'New password is required';
      ok = false;
    } else if (newPassword.length < 6) {
      e.newPassword = 'Password must be at least 6 characters';
      ok = false;
    }

    if (!confirmPassword.trim()) {
      e.confirmPassword = 'Please confirm the password';
      ok = false;
    } else if (newPassword !== confirmPassword) {
      e.confirmPassword = 'Passwords do not match';
      ok = false;
    }

    setErrors(e);
    return ok;
  };

  const handleReset = async () => {
    if (!validate()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/v1/reset-password', {
        user_id:          userId,
        new_password:     newPassword,
        confirm_password: confirmPassword,
      });

      if (response.data.success) {
        setSuccess(true);
        onSuccess?.();
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to reset password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setErrors({ newPassword: '', confirmPassword: '' });
    setError(null);
    setSuccess(false);
    onHide();
  };

  const passwordStrength = (() => {
    if (newPassword.length === 0)  return null;
    if (newPassword.length < 6)   return { level: 1, label: 'Too short',  color: '#ef4444' };
    if (newPassword.length < 9)   return { level: 2, label: 'Weak',       color: '#f97316' };
    if (newPassword.length < 12)  return { level: 3, label: 'Good',       color: '#eab308' };
    return                               { level: 4, label: 'Strong',     color: '#22c55e' };
  })();

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '480px' }}
      dismissableMask
      draggable={false}
      closable={!loading}
      pt={{
        root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 60px -12px rgba(0,0,0,0.3)' } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: 0 } },
      }}
    >
      <div>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(135deg, ${meta.color} 0%, #1a5555 50%, #0a2f2f 100%)`,
          padding: '28px 28px 24px',
          position: 'relative',
          overflow: 'hidden',
          color: 'white',
        }}>
          {/* Decorative blobs */}
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '140px', height: '140px', background: 'rgba(212,241,126,0.12)', borderRadius: '50%', filter: 'blur(35px)' }} />
          <div style={{ position: 'absolute', bottom: '-60px', left: '-30px', width: '160px', height: '160px', background: 'rgba(212,241,126,0.07)', borderRadius: '50%', filter: 'blur(40px)' }} />

          {/* Close button */}
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'rgba(255,255,255,0.12)', border: 'none',
              borderRadius: '8px', width: '32px', height: '32px',
              color: 'white', cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8rem', zIndex: 2,
            }}
          >
            <i className="pi pi-times" />
          </button>

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '4px 10px',
              background: 'rgba(212,241,126,0.2)', borderRadius: '16px',
              marginBottom: '16px', fontSize: '0.65rem', fontWeight: '700',
              letterSpacing: '0.8px', textTransform: 'uppercase',
            }}>
              <i className={`pi ${meta.icon}`} style={{ fontSize: '0.65rem' }} />
              {meta.label} Password Reset
            </div>

            {/* Lock icon + title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '14px',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <i className="pi pi-lock" style={{ fontSize: '1.4rem', color: '#d4f17e' }} />
              </div>
              <div>
                <h2 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', fontWeight: '700', color: 'white' }}>
                  Reset Password
                </h2>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)' }}>
                  Setting new password for{' '}
                  <span style={{ color: '#d4f17e', fontWeight: '600' }}>{name}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div style={{ padding: '24px 28px 28px', background: 'white' }}>

          {/* Success state */}
          {success ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 8px 24px rgba(16,185,129,0.25)',
              }}>
                <i className="pi pi-check" style={{ fontSize: '1.8rem', color: '#065f46' }} />
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '700', color: '#065f46' }}>
                Password Reset Successfully!
              </h3>
              <p style={{ margin: '0 0 24px 0', fontSize: '0.85rem', color: '#6b7280' }}>
                The password for <strong>{name}</strong> has been updated.
                They can now log in with the new password.
              </p>
              <GlobalButton
                label="Close"
                variant="primary"
                icon="pi pi-check-circle"
                onClick={handleClose}
              />
            </div>
          ) : (
            <>
              {/* Info notice */}
              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                border: '1px solid #bbf7d0',
                borderRadius: '12px', padding: '14px 16px',
                marginBottom: '20px',
                display: 'flex', alignItems: 'flex-start', gap: '10px',
              }}>
                <i className="pi pi-info-circle" style={{ color: '#16a34a', fontSize: '1rem', marginTop: '1px' }} />
                <div>
                  <p style={{ margin: '0 0 2px 0', fontSize: '0.8rem', fontWeight: '700', color: '#15803d' }}>
                    Resetting password for {meta.label}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#166534', lineHeight: '1.4' }}>
                    The new password will be applied immediately. Inform {name} after resetting.
                  </p>
                </div>
              </div>

              {/* API error */}
              {error && (
                <Message
                  severity="error"
                  text={error}
                  style={{ marginBottom: '16px', fontSize: '0.85rem', width: '100%' }}
                />
              )}

              {/* Fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <GlobalInputField
                  label="New Password"
                  required
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); clearFieldError('newPassword'); }}
                  error={errors.newPassword}
                  type="password"
                  icon="pi pi-lock"
                />
                <GlobalInputField
                  label="Confirm Password"
                  required
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); clearFieldError('confirmPassword'); }}
                  error={errors.confirmPassword}
                  type="password"
                  icon="pi pi-lock"
                />
              </div>

              {/* Hint */}
              <p style={{ margin: '12px 0 16px 0', fontSize: '0.72rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <i className="pi pi-shield" style={{ fontSize: '0.68rem' }} />
                Minimum 6 characters required
              </p>

              {/* Strength indicator */}
              {passwordStrength && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} style={{
                        flex: 1, height: '4px', borderRadius: '2px',
                        background: i <= passwordStrength.level ? passwordStrength.color : '#e5e7eb',
                        transition: 'background 0.3s ease',
                      }} />
                    ))}
                  </div>
                  <p style={{ margin: 0, fontSize: '0.7rem', color: passwordStrength.color, fontWeight: '600' }}>
                    {passwordStrength.label}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <GlobalButton
                  label="Cancel"
                  variant="outline"
                  icon="pi pi-times"
                  onClick={handleClose}
                  disabled={loading}
                />
                <GlobalButton
                  label={loading ? 'Resetting...' : 'Reset Password'}
                  variant="primary"
                  icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                  onClick={handleReset}
                  disabled={loading}
                  loading={loading}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default AdminResetPasswordModal;